import React, { useEffect, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import { RootState } from "src/redux/reducers";
import { Button } from "src/app/components/Button/Button";
import { ReactComponent as ShareIcon } from "src/images/share-alt.svg";
import { API_URLS } from "src/utils/API_URLS";
import { showErrorToast } from "src/utils/methods";
import { InsightsDashboardTableData } from "src/app/components/InsightsDashboard/InsightsDashboard";
import { IDPublications } from "src/app/components/InsightsDashboard/components/IDPublications/IDPublications";
import { HomepagePublicationCard } from "src/app/homepage/components/HomepagePublicationCard/HomepagePublicationCard";
import { InsightsDashboardSummary } from "src/app/components/InsightsDashboard/components/InsightsDashboardSummary/InsightsDashboardSummary";
import { getWelcomePageData } from "src/redux/main/mainActions";
import { getActivityList } from "src/redux/activity/activityActions";
import { ActivityItem } from "src/app/components/Activity/components/ActivityItem/ActivityItem";
import WelcomeQuickStart from "../Welcome/sections/WelcomeQuickStart/WelcomeQuickStart";
import WelcomeDiscoverMore from "../Welcome/sections/WelcomeDiscoverMore/WelcomeDiscoverMore";
import IDHFormattedMessage from "../components/IDHFormattedMessage/IDHFormattedMessage";
import Skeleton from "../components/Skeleton/Skeleton";
import HorizontalScroller from "../components/Tabs/components/HorizontalScroller";
import "./Homepage.scss";
import { getRoute, ROUTES } from "src/utils/ROUTES";
import PageHeader from "../components/PageHeader/PageHeader";
import { setPlanUpgradeNeededModalContext } from "../../redux";
import { useCheckFeatureAccess } from "../subscription/subscriptionFunctions";
import { SubscriptionEntitlements } from "../subscription/subscriptionUtils";

const HOMEPAGE_ACTIVITY_LOGS_FETCH_LIMIT = 100;
const HOMEPAGE_ACTIVITY_LOGS_DISPLAY_LIMIT = 5;
const EMPTY_ACTIVITY_FILTERS = {
  campaigns: [],
  members: [],
  dateRange: null,
};

const Homepage: React.FC = () => {
  const dispatch = useDispatch();

  const { activeWorkspaceUuid, identity } = useSelector(
    (state: RootState) => state.mainReducer,
  );
  const { isWorkspaceHostnameCustomized } = identity;
  const { activityList, loading: activityLoading } = useSelector(
    (state: RootState) => state.activityReducer,
  );

  const [publicationsData, setPublicationsData] = useState<
    InsightsDashboardTableData["top"] | null
  >(null);
  const [totalData, setTotalData] = useState<
    InsightsDashboardTableData["total"] | null
  >(null);
  const [comparisonTotalData, setComparisonTotalData] = useState<
    InsightsDashboardTableData["total"] | null
  >(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const socialInsightsEntitlementGranted = useCheckFeatureAccess(
    SubscriptionEntitlements.SocialInsights,
  );

  useEffect(() => {
    if (!activeWorkspaceUuid || isWorkspaceHostnameCustomized) return;

    dispatch(getWelcomePageData(activeWorkspaceUuid));
  }, [activeWorkspaceUuid, isWorkspaceHostnameCustomized, dispatch]);

  useEffect(() => {
    if (!activeWorkspaceUuid) return;

    dispatch(
      getActivityList(
        activeWorkspaceUuid,
        EMPTY_ACTIVITY_FILTERS,
        0,
        HOMEPAGE_ACTIVITY_LOGS_FETCH_LIMIT,
      ),
    );
  }, [activeWorkspaceUuid, dispatch]);

  useEffect(() => {
    if (!activeWorkspaceUuid) return;

    axios
      .get(API_URLS.getHomepagePublicationStats)
      .then(({ data }) => {
        setTotalData(data.content.total);
        setPublicationsData(data.content.top);
        setComparisonTotalData(data.content.comparisonTotal);
      })
      .catch(() => {
        showErrorToast();
      })
      .finally(() => {
        setStatsLoading(false);
      });
  }, [activeWorkspaceUuid]);

  const hasAnyResults = (totalData?.posts_count ?? 0) > 0;

  if (
    !identity.permissions?.workspace.includes("view_dashboard") &&
    activeWorkspaceUuid
  ) {
    return (
      <Redirect
        to={getRoute(ROUTES.PROJECTS, { wsWorkspaceUuid: activeWorkspaceUuid })}
      />
    );
  }

  return (
    <div className="homepage">
      <PageHeader
        className="homepage__header"
        title={
          <IDHFormattedMessage id="ws_homepage_title" defaultMessage="Home" />
        }
        description={
          (statsLoading || hasAnyResults) && (
            <IDHFormattedMessage
              id="ws_homepage_description"
              defaultMessage="Everything happening in your workspace, at a glance."
            />
          )
        }
      />

      {statsLoading ? (
        <>
          <HomepageStatsSkeleton />
          <HomepageGallerySkeleton />
        </>
      ) : (
        <>
          {hasAnyResults && (
            <InsightsDashboardSummary
              data={totalData}
              comparisonData={comparisonTotalData}
              selectedDateRange={null}
              columns={6}
              actions={
                !isWorkspaceHostnameCustomized &&
                (socialInsightsEntitlementGranted ? (
                  <Link
                    to={getRoute(ROUTES.SOCIAL_INSIGHTS, {
                      wsWorkspaceUuid: activeWorkspaceUuid,
                    })}
                  >
                    <Button variant="white-with-light-grey-border" size="small">
                      <IDHFormattedMessage
                        id="ws_show_more"
                        defaultMessage="Show more"
                      />
                      <ShareIcon />
                    </Button>
                  </Link>
                ) : (
                  <Button
                    variant="white-with-light-grey-border"
                    size="small"
                    onClick={() =>
                      dispatch(
                        setPlanUpgradeNeededModalContext(
                          SubscriptionEntitlements.SocialInsights,
                        ),
                      )
                    }
                  >
                    <IDHFormattedMessage
                      id="ws_show_more"
                      defaultMessage="Show more"
                    />
                    <ShareIcon />
                  </Button>
                ))
              }
            />
          )}
          {hasAnyResults && publicationsData && (
            <IDPublications
              data={{
                date: publicationsData.postsByDate,
                er: publicationsData.postsByEr,
              }}
              showControls={false}
              titleId="ws_homepage_publications"
              titleDefaultMessage="Publications"
              renderItem={(item, index, onThumbnailClick) => (
                <HomepagePublicationCard
                  data={item}
                  index={index}
                  onThumbnailClick={onThumbnailClick}
                />
              )}
            />
          )}
        </>
      )}

      {!isWorkspaceHostnameCustomized && <WelcomeQuickStart />}

      {activityLoading ? (
        <HomepageActivitySkeleton />
      ) : (
        activityList.length > 0 && (
          <div className="homepage-activity">
            <span className="homepage-activity__header">
              <IDHFormattedMessage id="ws_activity" defaultMessage="Activity" />

              <Link to={`/workspace/${activeWorkspaceUuid}/activity`}>
                <Button variant="white-with-light-grey-border" size="small">
                  <IDHFormattedMessage
                    id="ws_show_more"
                    defaultMessage="Show more"
                  />
                  <ShareIcon />
                </Button>
              </Link>
            </span>
            <div className="homepage-activity__list">
              {activityList
                .slice(0, HOMEPAGE_ACTIVITY_LOGS_DISPLAY_LIMIT)
                .map((log: any) => (
                  <ActivityItem data={log} key={log?.createdDate?.date} />
                ))}
            </div>
          </div>
        )
      )}

      {!isWorkspaceHostnameCustomized && <WelcomeDiscoverMore />}
    </div>
  );
};

const HOMEPAGE_SUMMARY_SKELETON_COLUMNS = 6;
const HOMEPAGE_SUMMARY_SKELETON_ITEMS = 12;

const HomepageStatsSkeleton: React.FC = () => (
  <div className="insights-dashboard-summary homepage-skeleton-summary">
    <div className="insights-dashboard-summary__header">
      <Skeleton variant="text" width={120} height={24} />
      <Skeleton variant="rect" width={96} height={32} borderRadius={8} />
    </div>
    <div
      className="insights-dashboard-summary__content"
      style={
        {
          "--summary-columns": HOMEPAGE_SUMMARY_SKELETON_COLUMNS,
        } as React.CSSProperties
      }
    >
      {Array.from({ length: HOMEPAGE_SUMMARY_SKELETON_ITEMS }).map(
        (_, index) => (
          <div className="insights-dashboard-summary__item" key={index}>
            <Skeleton variant="text" width={70} height={14} />
            <Skeleton variant="text" width={56} height={28} />
          </div>
        ),
      )}
    </div>
  </div>
);

const HOMEPAGE_GALLERY_SKELETON_ITEMS = 8;

const HomepageGallerySkeleton: React.FC = () => (
  <div className="id-publications homepage-skeleton-gallery">
    <div className="id-publications__header">
      <Skeleton variant="text" width={140} height={24} />
    </div>
    <HorizontalScroller disableWheelScroll>
      <div className="id-publications__container">
        {Array.from({ length: HOMEPAGE_GALLERY_SKELETON_ITEMS }).map(
          (_, index) => (
            <div className="gallery-item-wrapper" key={index}>
              <div className="gallery-item">
                <div className="gallery-item__provider">
                  <div className="gallery-item__provider-header">
                    <Skeleton variant="circle" width={20} height={20} />
                    <Skeleton variant="text" width={90} height={14} />
                  </div>
                  <Skeleton variant="circle" width={20} height={20} />
                </div>
                <div className="gallery-item__thumbnail">
                  <Skeleton variant="rect" width="100%" height="100%" />
                </div>
                <Skeleton variant="text" width="60%" height={14} />
              </div>
            </div>
          ),
        )}
      </div>
    </HorizontalScroller>
  </div>
);

const HOMEPAGE_ACTIVITY_SKELETON_ROWS = HOMEPAGE_ACTIVITY_LOGS_DISPLAY_LIMIT;

const HomepageActivitySkeleton: React.FC = () => (
  <div className="homepage-activity homepage-skeleton-activity">
    <span className="homepage-activity__header">
      <Skeleton variant="text" width={100} height={24} />
    </span>
    <div className="homepage-activity__list">
      {Array.from({ length: HOMEPAGE_ACTIVITY_SKELETON_ROWS }).map(
        (_, index) => (
          <div className="activity__table-element" key={index}>
            <div className="activity__table-element-activity">
              <Skeleton
                variant="circle"
                width={24}
                height={24}
                marginRight={8}
              />
              <Skeleton variant="text" width="60%" height={14} />
            </div>
            <div className="activity__table-element-time">
              <Skeleton variant="text" width={80} height={14} />
            </div>
          </div>
        ),
      )}
    </div>
  </div>
);

export default Homepage;
